import { useMemo, useRef, useEffect, useCallback } from 'react'
import Map, { Source, Layer, NavigationControl } from 'react-map-gl/mapbox'
import 'mapbox-gl/dist/mapbox-gl.css'

// PSG Prestige deal type colors — kept identical to v1
const COLORS = {
  acquisition: '#3B82F6',
  development: '#2ECC71',
  lending:     '#D4AF37',
  unknown:     '#94A3B8',
}

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

// ── Layer paint specs ────────────────────────────────────────────────────────

const clusterLayer = {
  id: 'clusters',
  type: 'circle',
  filter: ['has', 'point_count'],
  paint: {
    'circle-color':          'rgba(212,175,55,0.15)',
    'circle-stroke-color':   'rgba(212,175,55,0.4)',
    'circle-stroke-width':   1,
    'circle-radius':         ['step', ['get', 'point_count'], 14, 20, 17, 100, 20],
  },
}

const clusterCountLayer = {
  id: 'cluster-count',
  type: 'symbol',
  filter: ['has', 'point_count'],
  layout: {
    'text-field':  '{point_count_abbreviated}',
    'text-font':   ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size':   11,
  },
  paint: {
    'text-color': '#D4AF37',
  },
}

const unclusteredLayer = {
  id: 'unclustered-point',
  type: 'circle',
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-color': [
      'match',
      ['get', 'type'],
      'acquisition', COLORS.acquisition,
      'development', COLORS.development,
      'lending',     COLORS.lending,
                     COLORS.unknown,
    ],
    'circle-radius':        ['case', ['boolean', ['feature-state', 'selected'], false], 8, 5],
    'circle-stroke-color':  'rgba(255,255,255,0.55)',
    'circle-stroke-width':  ['case', ['boolean', ['feature-state', 'selected'], false], 2, 1.5],
  },
}

// ── Component ────────────────────────────────────────────────────────────────

export default function MapView({ deals, selectedDeal, onSelectDeal }) {
  const mapRef = useRef(null)

  // Convert deals into a GeoJSON FeatureCollection
  const geojson = useMemo(() => ({
    type: 'FeatureCollection',
    features: deals
      .filter(d => d.lat != null && d.lng != null)
      .map(d => ({
        type: 'Feature',
        id:   d.id,
        properties: {
          id:     d.id,
          name:   d.name || '',
          type:   d.type || 'unknown',
          status: d.status || '',
        },
        geometry: {
          type: 'Point',
          coordinates: [Number(d.lng), Number(d.lat)],
        },
      })),
  }), [deals])

  // Fly to selected deal
  useEffect(() => {
    if (!selectedDeal || selectedDeal.lat == null || selectedDeal.lng == null) return
    const map = mapRef.current?.getMap()
    if (!map) return
    map.flyTo({
      center: [selectedDeal.lng, selectedDeal.lat],
      zoom:   Math.max(map.getZoom(), 11),
      duration: 900,
    })
  }, [selectedDeal])

  // Click handler — resolve cluster vs point
  const handleClick = useCallback((e) => {
    if (!e.features || !e.features.length) return
    const feat = e.features[0]

    if (feat.properties.cluster) {
      // Cluster clicked — zoom in
      const map = mapRef.current?.getMap()
      const clusterId = feat.properties.cluster_id
      const source = map.getSource('deals')
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return
        map.easeTo({ center: feat.geometry.coordinates, zoom, duration: 500 })
      })
      return
    }

    // Individual point — find the deal
    const dealId = feat.properties.id
    const deal = deals.find(d => d.id === dealId)
    if (deal) onSelectDeal(deal)
  }, [deals, onSelectDeal])

  if (!MAPBOX_TOKEN) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', background: '#0A192F', color: '#E74C3C', padding: 40, textAlign: 'center',
      }}>
        <div>
          <p style={{ fontWeight: 700, marginBottom: 8 }}>VITE_MAPBOX_TOKEN not set</p>
          <p style={{ color: '#94A3B8', fontSize: 12 }}>
            Add it to <code>dashboard/.env</code> — see <code>dashboard/.env.example</code>.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{ longitude: -96.5, latitude: 38.5, zoom: 4 }}
        mapStyle="mapbox://styles/mapbox/dark-v11"
        style={{ height: '100%', width: '100%' }}
        interactiveLayerIds={['clusters', 'unclustered-point']}
        onClick={handleClick}
        cursor="grab"
      >
        <Source
          id="deals"
          type="geojson"
          data={geojson}
          cluster
          clusterMaxZoom={12}
          clusterRadius={45}
        >
          <Layer {...clusterLayer} />
          <Layer {...clusterCountLayer} />
          <Layer {...unclusteredLayer} />
        </Source>
        <NavigationControl position="top-left" showCompass={false} />
      </Map>

      {/* PSG Legend — same content, positioned identically */}
      <div style={{
        position: 'absolute', bottom: 28, left: 12, zIndex: 1,
        background: 'rgba(6,16,32,0.9)',
        border: '1px solid rgba(212,175,55,0.2)',
        borderRadius: 8, padding: '10px 14px',
        pointerEvents: 'none',
      }}>
        {Object.entries(COLORS).filter(([k]) => k !== 'unknown').map(([type, color]) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%', background: color,
              border: '1px solid rgba(255,255,255,0.3)',
            }} />
            <span style={{
              color: '#FFFFFF', fontSize: 11, textTransform: 'capitalize',
              letterSpacing: '0.06em', fontWeight: 500,
            }}>{type}</span>
          </div>
        ))}
        <div style={{
          marginTop: 6, paddingTop: 6,
          borderTop: '1px solid rgba(255,255,255,0.07)',
          color: '#94A3B8', fontSize: 9, letterSpacing: '0.08em',
        }}>CLICK MARKER FOR DETAILS</div>
      </div>
    </div>
  )
}
